/**
 * HouseholdRecommendationHero — executive NTN Recommendation card for the
 * Household Profile.
 *
 * v1.0.5 CONTROL RULE: the subscriber-facing recommendation reads ONLY the
 * staff-POSTED `group.recommendation` (set via PATCH /groups/{gid}/recommendation,
 * NTN staff only, audited). Until staff explicitly post one, a neutral
 * "Under NTN Review" state renders — never an automatic verdict.
 * The system-calculated rollup (`household.suggested_verdict`, present only
 * in NTN-staff payloads) is internal decision support and is clearly labeled.
 */
import { useState } from "react";
import { apiClient, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Gavel } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/dateFormat";

const STATE_HERO = {
  approve: {
    headline: "Recommended",
    subline: "NTN recommends this household proceed. The final leasing decision rests with the property manager.",
    cls: "bg-gradient-to-br from-emerald-800 to-emerald-700 text-white border-emerald-900 shadow-lg shadow-emerald-900/20",
    dark: true,
  },
  conditional: {
    headline: "Recommended with Conditions",
    subline: "NTN recommends proceeding subject to the conditions noted. The final leasing decision rests with the property manager.",
    cls: "bg-gradient-to-br from-amber-700 to-amber-600 text-white border-amber-800 shadow-lg shadow-amber-900/20",
    dark: true,
  },
  deny: {
    headline: "Not Recommended",
    subline: "NTN does not recommend this household. The final leasing decision rests with the property manager.",
    cls: "bg-gradient-to-br from-rose-800 to-rose-700 text-white border-rose-900 shadow-lg shadow-rose-900/20",
    dark: true,
  },
  review: {
    headline: "Under NTN Review",
    subline: "Screening in progress. NTN will post the household recommendation once the required screening work is complete.",
    cls: "bg-gradient-to-br from-[#F3EFEA] to-white border-slate-200 text-slate-900",
    dark: false,
  },
};

const REC_LABEL = { approve: "Approve", conditional: "Approve with Conditions", deny: "Not Recommended" };

export default function HouseholdRecommendationHero({ group, isStaff = false, onPosted, testid = "household-recommendation-hero" }) {
  const h = group.household || {};
  const posted = group.recommendation || null;
  const state = STATE_HERO[posted] || STATE_HERO.review;
  const headlineCls = state.dark ? "text-white" : "text-[#0A192F]";
  const subCls = state.dark ? "text-white/80" : "text-[#4A5568]";
  const metaCls = state.dark ? "text-white/70" : "text-[#4A5568]";

  const [open, setOpen] = useState(false);
  const [rec, setRec] = useState(posted || "approve");
  const [reason, setReason] = useState(group.recommendation_reason || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await apiClient.patch(`/groups/${group.id}/recommendation`, { recommendation: rec, reason: reason || null });
      toast.success("Household recommendation posted");
      setOpen(false);
      onPosted?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`relative overflow-hidden rounded-lg border ${state.cls} p-6 sm:p-8`} data-testid={testid}>
      <div className={`absolute inset-0 dotted-bg pointer-events-none ${state.dark ? "opacity-15" : "opacity-30"}`} />
      <div className="relative flex flex-col sm:flex-row sm:items-start justify-between gap-5">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold mb-3 flex items-center gap-1.5">
            <span className={`h-1.5 w-1.5 rounded-full ${state.dark ? "bg-white/90" : "bg-slate-900"}`} />
            <span className={state.dark ? "text-white/90" : "text-slate-500"}>NTN Recommendation · Household</span>
          </div>
          <h2 className={`ntn-h1 leading-none ${headlineCls}`} data-testid={`${testid}-headline`}>
            {state.headline}
          </h2>
          <p className={`mt-4 max-w-2xl leading-relaxed ntn-body ${subCls}`}>{state.subline}</p>
          {posted && group.recommendation_reason && (
            <div className={`mt-4 max-w-2xl text-sm leading-relaxed rounded-sm px-4 py-3 ${state.dark ? "bg-white/10 text-white/90" : "bg-[#F3EFEA] text-[#0A192F]"}`}
                 data-testid={`${testid}-reason`}>
              {group.recommendation_reason}
            </div>
          )}
          {posted && group.recommendation_by_name && (
            <div className={`ntn-mono mt-4 uppercase ${metaCls}`} data-testid={`${testid}-attribution`}>
              Posted by {group.recommendation_by_name}{group.recommendation_at ? ` · ${fmtDate(group.recommendation_at)}` : ""}
            </div>
          )}
          {!posted && h.members_count != null && (
            <div className={`ntn-mono mt-4 uppercase ${metaCls}`}>
              {h.members_count} member{h.members_count !== 1 ? "s" : ""} under review
            </div>
          )}
          {isStaff && (
            <div className="mt-5 flex flex-wrap items-center gap-3">
              {h.suggested_verdict && (
                <span className={`inline-flex items-center gap-1.5 text-[10px] font-mono font-bold tracking-[0.14em] uppercase px-2 py-1 rounded-sm ring-1 ring-inset ${state.dark ? "bg-white/10 text-white/90 ring-white/20" : "bg-[#F3EFEA] text-[#4A5568] ring-[#0A192F]/10"}`}
                      data-testid={`${testid}-system-suggestion`}>
                  System-calculated suggestion: {h.suggested_verdict.toUpperCase()}
                </span>
              )}
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant={state.dark ? "secondary" : "default"}
                          className={state.dark ? "" : "bg-[#0A192F] text-white hover:bg-[#0A192F]/90"}
                          data-testid="post-group-recommendation-btn">
                    <Gavel className="h-3.5 w-3.5 mr-1.5" />
                    {posted ? "Update recommendation" : "Post household recommendation"}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md" data-testid="post-group-recommendation-dialog">
                  <DialogHeader>
                    <DialogTitle>Post household recommendation</DialogTitle>
                    <DialogDescription>
                      This is a deliberate NTN decision. It is attributed to you, audit-logged, and the subscriber is notified.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-1">
                    <div className="space-y-1.5">
                      <Label>Recommendation</Label>
                      <Select value={rec} onValueChange={setRec}>
                        <SelectTrigger data-testid="group-recommendation-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(REC_LABEL).map(([k, v]) => (
                            <SelectItem key={k} value={k} data-testid={`group-recommendation-option-${k}`}>{v}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Reason / conditions (optional)</Label>
                      <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                                placeholder="Context the property manager should see…"
                                data-testid="group-recommendation-reason" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={submit} disabled={busy} className="bg-[#0A192F] text-white hover:bg-[#0A192F]/90"
                            data-testid="group-recommendation-submit">
                      {busy ? "Posting…" : "Post recommendation"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>
        <Gavel className={`h-8 w-8 shrink-0 ${state.dark ? "text-white/30" : "text-slate-300"}`} strokeWidth={1.5} />
      </div>
    </div>
  );
}
